/**
 * The landing page's music switch.
 *
 * The marketing page had no sound at all, and the owner wants a bed on it. This
 * deliberately does NOT add a second audio system: it drives the same engine the
 * game uses (`audio.ts`), so the bed cross-fades, obeys the same volumes, and the
 * visitor's choice is already in `terraboma:audio` by the time they sign in.
 *
 * Three decisions worth writing down, because they look like omissions otherwise:
 *
 * - **It starts OFF, every visit.** Browsers block audio until a gesture anyway,
 *   so a marketing page that "tries" to autoplay just lies to its own button. The
 *   view stays `none` until the speaker is actually pressed, which means even a
 *   returning visitor whose settings say un-muted arrives in silence.
 * - **One speaker, no sliders.** A landing page is not a settings screen; the
 *   full mute + music + effects rows already exist in `audio-controls.ts` for the
 *   places that want them.
 * - **The speaker owns the landing bed, not the master mute.** Turning it off stops
 *   the landing music without muting the game. Turning it ON does lift an existing
 *   master mute, because a visitor who presses play means it. Persisted through the
 *   shared settings, so the visitor who silenced the trailer is not greeted by
 *   music again one click later inside the game.
 */

import { getAudioSettings, initAudio, setAudioSettings, setAudioView } from './audio';

const ON = 'M4 9v6h3.5L13 20V4L7.5 9H4z M16.5 8.5a5 5 0 0 1 0 7 M19 6a8.5 8.5 0 0 1 0 12';
const OFF = 'M4 9v6h3.5L13 20V4L7.5 9H4z M17 9.5l5 5 M22 9.5l-5 5';

function icon(on: boolean): SVGSVGElement {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', '20');
  svg.setAttribute('height', '20');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', 'currentColor');
  svg.setAttribute('stroke-width', '1.8');
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', on ? ON : OFF);
  svg.appendChild(path);
  return svg;
}

/**
 * Mount the speaker into the landing page and return its teardown.
 *
 * Teardown clears the view rather than only removing the button: leaving the
 * landing for the login screen must take the bed with it, the same way clearing
 * an overlay hands the farm its music back.
 */
export function mountLandingAudio(root: HTMLElement): () => void {
  // The engine's unlock listeners have to be armed before the first press, or the
  // press that turns the music on is the very gesture that gets swallowed.
  initAudio();

  let on = false;                       // "is the landing bed playing?", not "is sound allowed?"
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.dataset.landingAudio = 'toggle';
  btn.setAttribute(
    'style',
    'position:fixed;right:14px;bottom:14px;z-index:60;width:44px;height:44px;' +
    'display:flex;align-items:center;justify-content:center;border-radius:9999px;' +
    'border:1px solid rgba(232,196,106,.45);background:rgba(18,16,14,.82);' +
    'color:#e8c46a;cursor:pointer;backdrop-filter:blur(4px)',
  );

  function paint(): void {
    btn.innerHTML = '';
    btn.appendChild(icon(on));
    btn.setAttribute('aria-pressed', String(on));
    btn.setAttribute('aria-label', on ? 'Turn music off' : 'Turn music on');
    btn.title = on ? 'Music on' : 'Music off';
  }

  btn.addEventListener('click', () => {
    on = !on;
    if (on) {
      // A visitor who arrives with the game muted and presses PLAY means it: the
      // press has to lift the mute, or the bed starts at volume zero and the
      // button reads as broken.
      if (getAudioSettings().muted) setAudioSettings({ muted: false });
      setAudioView('landing');
    } else {
      // Drop the landing bed only. This used to write `muted: true`, which is the
      // engine's MASTER mute — so silencing the marketing page also silenced every
      // sound effect once the visitor started playing.
      setAudioView('none');
    }
    paint();
  });

  paint();
  root.appendChild(btn);

  return () => {
    btn.remove();
    setAudioView('none');
  };
}
